import React, { memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Layer, Source } from 'react-map-gl';

import { VISUALISATION_TYPES } from 'utils/constants/visualisationTypes';

import { heatmapLayer } from './heatmapLayer';
import { prepareHeatmapData } from './heatmapData';
import { heatmapClusterLayer, heatmapClusterCountLayer } from './heatmapClusterLayer';
import { CLUSTER_MAX_ZOOM, CLUSTER_RADIUS } from './constants';

const { HEATMAP } = VISUALISATION_TYPES;

const HeatmapLayer = ({
  heatmapGradient,
  visualizationType,
  datapointsGeojson,
  heatmapWeightField,
  heatmapContrast,
  heatmapVisibility,
  aggregatableFields,
  color,
}) => {
  const preparedDatapointsGeojson = useMemo(() => prepareHeatmapData({
    datapointsGeojson,
    heatmapWeightField,
    aggregatableFields,
  }), [aggregatableFields, datapointsGeojson, heatmapWeightField]);

  if (visualizationType !== HEATMAP || !datapointsGeojson) {
    return null;
  }

  return (
    <>
      <Source
        id="heatmap-cluster-source"
        type="geojson"
        data={preparedDatapointsGeojson}
        cluster
        clusterMaxZoom={CLUSTER_MAX_ZOOM}
        clusterRadius={CLUSTER_RADIUS}
      >
        <Layer {...heatmapClusterLayer(color)} />
        <Layer {...heatmapClusterCountLayer(color)} />
      </Source>
      <Source id="heatmap-point-source" type="geojson" data={preparedDatapointsGeojson}>
        <Layer
          {...heatmapLayer(heatmapGradient, heatmapContrast, heatmapVisibility)}
        />
      </Source>
    </>
  );
};

HeatmapLayer.propTypes = {
  datapointsGeojson: PropTypes.object,
  color: PropTypes.string.isRequired,
  heatmapWeightField: PropTypes.string,
  heatmapContrast: PropTypes.number,
  heatmapVisibility: PropTypes.number,
  aggregatableFields: PropTypes.array.isRequired,
  visualizationType: PropTypes.string.isRequired,
  heatmapGradient: PropTypes.shape({
    lowestValue: PropTypes.string,
    midpointValue: PropTypes.string,
    highestValue: PropTypes.string,
  }).isRequired,
};

export default memo(HeatmapLayer);
